import { useEffect, useRef, useState } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { motion, useInView, useMotionValue, useSpring } from "framer-motion";

/* ── Fonts ─────────────────────────────────────────────────── */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
`;

/* ── Palette & tokens ───────────────────────────────────────── */
const C = {
  bg: "#ffffff",
  card: "#141414",
  border: "#e9e9e9",
  accent: "#3676be", // electric lime
  accentDim: "#2a8bcc",
  text: "#332f2f",
  muted: "#666",
};

/* ── Keyframes ──────────────────────────────────────────────── */
const scanline = keyframes`
  0%   { transform: translateY(-100%); }
  100% { transform: translateY(400%); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 ${C.accent}55; }
  50%       { box-shadow: 0 0 0 10px ${C.accent}00; }
`;

/* ── Styled components ──────────────────────────────────────── */
const Section = styled.section`
  padding: 1em 24px 96px 24px;
  position: relative;
  overflow: hidden;

  /* subtle grid texture */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(${C.border} 1px, transparent 1px),
      linear-gradient(90deg, ${C.border} 1px, transparent 1px);
    background-size: 48px 48px;
    opacity: 0.35;
    pointer-events: none;
  }
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const Label = styled(motion.p)`
  font-family: "DM Sans", sans-serif;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.accent};
  text-align: center;
  margin-bottom: 26px;

  &::before,
  &::after {
    content: "—";
    margin: 0 10px;
    color: ${C.accentDim};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

/* vertical dividers */
const Card = styled(motion.div)`
  position: relative;
  padding: 48px 40px;
  text-align: center;
  cursor: default;

  /* right border on first two */
  &:not(:last-child)::after {
    content: "";
    position: absolute;
    right: 0;
    top: 20%;
    height: 60%;
    width: 1px;
    background: linear-gradient(
      to bottom,
      transparent,
      ${C.border},
      transparent
    );
  }

  @media (max-width: 680px) {
    &:not(:last-child)::after {
      top: auto;
      right: auto;
      bottom: 0;
      left: 20%;
      height: 1px;
      width: 60%;
      background: linear-gradient(
        to right,
        transparent,
        ${C.border},
        transparent
      );
    }
  }
`;

const IconWrap = styled(motion.div)`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  border: 1px solid ${C.border};
  background: ${C.card};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 28px;
  font-size: 22px;
  position: relative;
  animation: ${pulse} 3s ease-in-out infinite;

  /* scanline shine */
  overflow: hidden;
  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 30%;
    background: linear-gradient(transparent, ${C.accent}22, transparent);
    animation: ${scanline} 3s ease-in-out infinite;
  }
`;

const BigNumber = styled.div`
  font-family: "Bebas Neue", sans-serif;
  font-size: clamp(64px, 8vw, 96px);
  line-height: 1;
  color: ${C.text};
  letter-spacing: -0.01em;

  span.accent {
    color: ${C.accent};
  }
`;

const StatLabel = styled.p`
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${C.muted};
  margin-top: 10px;
`;

const Underline = styled(motion.div)`
  height: 2px;
  background: ${C.accent};
  border-radius: 2px;
  margin: 16px auto 0;
  width: 32px;
`;

/* ── Animated counter hook ──────────────────────────────────── */
function useCounter(target, duration = 1.8, startCounting) {
  const [display, setDisplay] = useState(0);
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { duration: duration * 1000, bounce: 0 });

  useEffect(() => {
    if (!startCounting) return;
    mv.set(target);
  }, [startCounting, target]);

  useEffect(() => {
    return spring.on("change", (v) => setDisplay(Math.round(v)));
  }, [spring]);

  return display;
}

/* ── Stat data ──────────────────────────────────────────────── */
const STATS = [
  {
    icon: "📡",
    prefix: "",
    suffix: "+",
    value: 25,
    label: "Brendet e Suportuara",
    detail: "Universal & OEM compatible",
  },
  {
    icon: "😊",
    prefix: "",
    suffix: "+",
    value: 400,
    label: "Klientë të kënaqur",
    detail: "Shtepi & Hotele & Biznese",
  },
  {
    icon: "⚡",
    prefix: "",
    suffix: "%",
    value: 99,
    label: "Compatibility Rate",
    detail: "Punon për 99% të rasteve",
  },
];

/* ── Single stat card ───────────────────────────────────────── */
function StatCard({ stat, index, startCounting }) {
  const count = useCounter(stat.value, 1.6 + index * 0.2, startCounting);

  return (
    <Card
      initial={{ opacity: 0, y: 30 }}
      animate={startCounting ? { opacity: 1, y: 0 } : {}}
      transition={{
        delay: index * 0.15,
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      <IconWrap
        whileHover={{ scale: 1.1, rotate: -4 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        {stat.icon}
      </IconWrap>

      <BigNumber>
        {stat.prefix}
        {count}
        <span className="accent">{stat.suffix}</span>
      </BigNumber>

      <StatLabel>{stat.label}</StatLabel>

      <Underline
        initial={{ scaleX: 0 }}
        animate={startCounting ? { scaleX: 1 } : {}}
        transition={{
          delay: 0.4 + index * 0.15,
          duration: 0.5,
          ease: "easeOut",
        }}
      />

      <motion.p
        style={{
          fontFamily: "'Montserrat', sans-serif",
          fontSize: 12,
          color: "#868686",
          marginTop: 10,
          letterSpacing: "0.04em",
        }}
        initial={{ opacity: 0 }}
        animate={startCounting ? { opacity: 1 } : {}}
        transition={{ delay: 0.6 + index * 0.15 }}
      >
        {stat.detail}
      </motion.p>
    </Card>
  );
}

/* ── Main export ────────────────────────────────────────────── */
export default function Stats() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <>
      <GlobalStyle />
      <Section ref={ref}>
        <Inner>
          <Label
            initial={{ opacity: 0, y: -10 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            Statistikat tona
          </Label>

          <Grid>
            {STATS.map((stat, i) => (
              <StatCard
                key={stat.label}
                stat={stat}
                index={i}
                startCounting={inView}
              />
            ))}
          </Grid>
        </Inner>
      </Section>
    </>
  );
}
